/* ──────────────────────────────────────────────────────────────
 *  useComputeValidation — Pre-compute validation hook
 *
 *  Encapsulates all business rules that must pass before a
 *  compute request can be dispatched. Returns a `validate()`
 *  function that reads the latest store state and returns
 *  an array of blocking error messages (empty = valid).
 *
 *  Separation of concerns:
 *  • useInputValidation  → real-time field-level UX feedback
 *  • useComputeValidation → pre-submit blocking guard
 * ────────────────────────────────────────────────────────────── */

import { useCallback } from 'react';
import { useInputStore } from '@/stores/useInputStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { isByd } from '@/constants/bushingTypes';

export interface ComputeValidationResult {
  /** True when all checks pass and compute may proceed. */
  valid: boolean;
  /** Blocking messages to display in the guard dialog. */
  messages: string[];
}

/**
 * useComputeValidation — returns a stable `validate` function that
 * evaluates current store state against all pre-compute business rules.
 */
export function useComputeValidation() {
  const validate = useCallback((): ComputeValidationResult => {
    const { packaging, target } = useInputStore.getState();
    const bushingType = useBushingStore.getState().bushingType;
    const byd = isByd(bushingType);
    const isC1 = bushingType === 'C1';

    const missing: string[] = [];
    const errors: string[] = [];

    // ── Required field checks ────────────────────────────────
    if (packaging.coreLength === null) missing.push('Core length');
    if (packaging.outerSleeveLength === null) missing.push('Sleeve length');
    if (packaging.controlArmId === null) missing.push('Control arm ID');
    if (packaging.coreId === null) missing.push('Core ID');

    if (byd) {
      if (packaging.coreOdMin === null) missing.push('Core OD');
      if (packaging.hotOd === null) missing.push('Hot OD');
    } else {
      if (packaging.coreOdMin === null) missing.push('Min OD');
    }

    if (isC1) {
      if (packaging.collarOd === null) missing.push('Collar OD');
      const axialTravel = target.linearTravel['axial_c1'] ?? null;
      if (axialTravel === null) missing.push('Linear Travel (Axial)');
    }

    // ── Weight pocket guard (non-BYD only) ───────────────────
    if (!byd) {
      const { weightPockets, minContactArea } = useConfigStore.getState();
      const { calculatedMinContactArea } = useInputStore.getState();
      const effectiveMinArea = minContactArea ?? calculatedMinContactArea;
      if (weightPockets && (effectiveMinArea === null || effectiveMinArea <= 0)) {
        errors.push(
          'Weight pockets are enabled; enter Clamp load and click Min. OD Compute to generate Minimum OD and Area before computing.',
        );
      }
    }

    // ── Cross-field business rules (non-BYD) ─────────────────
    if (!byd) {
      const { coreOdMin, coreOdMax, coreLength, outerSleeveLength, controlArmId, coreId } = packaging;

      if (coreOdMin !== null && coreOdMax !== null && coreOdMin > coreOdMax) {
        errors.push('Min Core OD should be less than or equal to Max Core OD.');
      }
      if (coreLength !== null && outerSleeveLength !== null && coreLength < outerSleeveLength) {
        errors.push('Core Length should be greater than or equal to Sleeve Length.');
      }
      if (controlArmId !== null && coreOdMax !== null && controlArmId <= coreOdMax) {
        errors.push('Control Arm ID should be greater than Max Core OD.');
      }
      if (coreId !== null && coreOdMin !== null && coreId >= coreOdMin) {
        errors.push('Core ID should be less than Min Core OD.');
      }
    }

    // ── Aggregate result ─────────────────────────────────────
    const messages: string[] = [];
    if (missing.length > 0) {
      messages.push(`Missing inputs: ${missing.join(', ')}.`);
    }
    messages.push(...errors);

    return { valid: messages.length === 0, messages };
  }, []);

  return { validate };
}
